import { StockData, ScenarioType } from './types';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

interface StockEntryProps {
  readonly stock: StockData;
  readonly selectedScenario: ScenarioType;
}

export function StockEntry({
  stock,
  selectedScenario
}: StockEntryProps) {
  const { symbol, scenarios } = stock;
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  // Get the selected scenario data
  const scenarioData = scenarios[selectedScenario];
  
  if (!scenarioData) return null;

  // Helper function to format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Helper function to get color based on value comparison
  const getValueColor = (value: number, compareTo: number) => {
    if (value > compareTo) return 'text-green-500';
    if (value < compareTo) return 'text-red-500';
    return 'text-theme-secondary';
  };

  // Use API currentPrice when available; fallback to analystsBasePE
  return (
    <tr
      className="border-b border-theme-tertiary hover:bg-theme-tertiary/30 transition-colors cursor-pointer"
      tabIndex={0}
      onClick={() => navigate(`/${symbol}/summary`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/${symbol}/summary`);
        }
      }}
    >
      <td className="py-4 px-6">
        <div className="flex items-center">
          {stock.logo && !imgError ? (
            <img
              src={stock.logo}
              alt={`${symbol} logo`}
              className="w-10 h-10 rounded-full mr-3 object-cover border border-theme-tertiary"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-theme-tertiary flex items-center justify-center mr-3">
              <span className="font-medium text-theme-primary">{symbol.charAt(0)}</span>
            </div>
          )}
          <div className="font-medium text-theme-primary text-lg">{symbol}</div>
        </div>
      </td>
      <td className="py-4 px-6 font-medium text-theme-primary text-right">
        {formatCurrency((typeof stock.currentPrice === 'number' ? stock.currentPrice : (scenarioData?.analystsBasePE || 0)))}
      </td>
      
      {/* Removed Analyst 1YR Target column to match header */}
      
      {/* Analyst 3YR Target */}
      <td className="py-4 px-6">
        <div className="flex flex-col items-end">
          <div className={`font-medium ${
            scenarioData.analystsThreeYearTargetPriceLowerBound > 0 ? 'text-green-500' : 'text-red-500'
          }`}>
            {formatCurrency(scenarioData.analystsThreeYearTargetPriceLowerBound)}
          </div>
          <div className="text-xs text-theme-secondary">
            to {formatCurrency(scenarioData.analystsThreeYearTargetPriceUpperBound)}
          </div>
        </div>
      </td>
      
      {/* Your 1YR Target (using 3YR data divided by 3) */}
      <td className="py-4 px-6">
        <div className="flex flex-col items-end">
          <div className={`font-medium ${
            (scenarioData.myThreeYearTargetPriceLowerBound / 3) > 0 ? 'text-green-500' : 'text-red-500'
          }`}>
            {formatCurrency(scenarioData.myThreeYearTargetPriceLowerBound / 3)}
          </div>
          <div className={`text-xs ${
            getValueColor(
              scenarioData.myThreeYearTargetPriceLowerBound / 3, 
              scenarioData.analystsThreeYearTargetPriceLowerBound / 3
            )
          }`}>
            to {formatCurrency(scenarioData.myThreeYearTargetPriceUpperBound / 3)}
          </div>
        </div>
      </td>
      
      {/* Your 3YR Target */}
      <td className="py-4 px-6">
        <div className="flex flex-col items-end">
          <div className={`font-medium ${
            scenarioData.myThreeYearTargetPriceLowerBound > 0 ? 'text-green-500' : 'text-red-500'
          }`}>
            {formatCurrency(scenarioData.myThreeYearTargetPriceLowerBound)}
          </div>
          <div className={`text-xs ${
            getValueColor(
              scenarioData.myThreeYearTargetPriceLowerBound,
              scenarioData.analystsThreeYearTargetPriceLowerBound
            )
          }`}>
            to {formatCurrency(scenarioData.myThreeYearTargetPriceUpperBound)}
          </div>
        </div>
      </td>
      
      {/* Your 5YR Target */}
      <td className="py-4 px-6">
        <div className="flex flex-col items-end">
          <div className={`font-medium ${
            scenarioData.myFiveYearTargetPriceLowerBound > 0 ? 'text-green-500' : 'text-red-500'
          }`}>
            {formatCurrency(scenarioData.myFiveYearTargetPriceLowerBound)}
          </div>
          <div className={`text-xs ${
            getValueColor(
              scenarioData.myFiveYearTargetPriceLowerBound,
              scenarioData.analystsFiveYearTargetPriceLowerBound
            )
          }`}>
            to {formatCurrency(scenarioData.myFiveYearTargetPriceUpperBound)}
          </div>
        </div>
      </td>
    </tr>
  );
}
